import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUrlParam, useUrlPanelSync } from '../hooks/useUrlParam';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import {
  Badge, Button, Card, Dropdown, Input, KebabMenu, SearchableSelect, SidePanel, Switch, Table, Tabs,
} from '../components/ui';
import type { TableColumn } from '../components/ui';
import { supabase } from '../lib/supabase';
import { formatDate, formatForDateInput, getCurrentDate } from '../utils/dateUtils';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type Freq = 'daily' | 'weekly' | 'monthly';
type Kind = 'operator' | 'agentic';

const TAB_DEFS: Array<{ value: Kind; label: string }> = [
  { value: 'operator', label: 'Operator Tasks' },
  { value: 'agentic', label: 'Agentic Tasks' },
];

type RecurringTask = {
  id: string;
  client_id: string;
  title: string;
  description: string | null;
  assigned_to: string | null;
  freq: Freq;
  interval_n: number;
  byweekday: number[] | null;
  bymonthday: number | null;
  start_date: string;
  end_date: string | null;
  active: boolean;
  generated_through: string;
  kind: Kind;
  auto_approve: boolean;
  client?: { name: string } | null;
};

interface FormState {
  client_id: string | null;
  title: string;
  description: string;
  assigned_to: string | null;
  freq: Freq;
  interval_n: string;
  byweekday: number[];
  bymonthday: string;
  start_date: string;
  end_date: string;
  auto_approve: boolean;
}

const emptyForm = (): FormState => ({
  client_id: null,
  title: '',
  description: '',
  assigned_to: null,
  freq: 'weekly',
  interval_n: '1',
  byweekday: [],
  bymonthday: '1',
  start_date: formatForDateInput(getCurrentDate()),
  end_date: '',
  auto_approve: false,
});

function cadenceLabel(t: RecurringTask): string {
  const every = t.interval_n > 1 ? `Every ${t.interval_n}` : 'Every';
  if (t.freq === 'daily') {
    return t.interval_n > 1 ? `${every} days` : 'Daily';
  }
  if (t.freq === 'weekly') {
    const days = (t.byweekday && t.byweekday.length > 0 ? t.byweekday : [])
      .slice()
      .sort((a, b) => a - b)
      .map((d) => DAY_LABELS[d])
      .join(', ');
    const base = t.interval_n > 1 ? `${every} weeks` : 'Weekly';
    return days ? `${base} on ${days}` : base;
  }
  const dom = t.bymonthday ?? Number(t.start_date.slice(8, 10));
  const base = t.interval_n > 1 ? `${every} months` : 'Monthly';
  return `${base} on day ${dom}`;
}

export default function Recurring() {
  const [tab, setTab] = useState<Kind>('operator');
  const [templates, setTemplates] = useState<RecurringTask[]>([]);
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);

  const [panelOpen, setPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [genDays, setGenDays] = useState('7');
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    const [tplRes, clientRes, userRes] = await Promise.all([
      supabase
        .from('recurring_tasks')
        .select('*, client:clients(name)')
        .order('created_at', { ascending: false }),
      supabase.from('clients').select('id, name').order('name'),
      supabase.from('users').select('id, name').order('name'),
    ]);
    if (!tplRes.error) setTemplates((tplRes.data as unknown as RecurringTask[]) || []);
    if (!clientRes.error) setClients((clientRes.data as Array<{ id: string; name: string }>) || []);
    if (!userRes.error) setUsers((userRes.data as Array<{ id: string; name: string }>) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setPanelOpen(true);
  };

  const openEdit = (t: RecurringTask) => {
    setEditingId(t.id);
    setForm({
      client_id: t.client_id,
      title: t.title,
      description: t.description ?? '',
      assigned_to: t.assigned_to,
      freq: t.freq,
      interval_n: String(t.interval_n),
      byweekday: t.byweekday ?? [],
      bymonthday: String(t.bymonthday ?? t.start_date.slice(8, 10).replace(/^0/, '')),
      start_date: t.start_date,
      end_date: t.end_date ?? '',
      auto_approve: t.auto_approve,
    });
    setFormError(null);
    setPanelOpen(true);
  };

  // The template being edited is ?edit=<id>, so refresh and back keep the panel; a new template's form stays local.
  const [editParam, setEditParam] = useUrlParam('edit');
  useUrlPanelSync(
    editParam,
    panelOpen ? editingId : null,
    !loading,
    (id) => {
      const target = templates.find((t) => t.id === id);
      if (target) openEdit(target);
    },
    () => setPanelOpen(false),
  );
  const closePanel = () => {
    setPanelOpen(false);
    if (editParam) setEditParam(null);
  };

  const toggleWeekday = (d: number) => {
    setForm((f) => ({
      ...f,
      byweekday: f.byweekday.includes(d)
        ? f.byweekday.filter((x) => x !== d)
        : [...f.byweekday, d],
    }));
  };

  const handleSave = async () => {
    if (!form.client_id) return setFormError('Pick a client');
    if (!form.title.trim()) return setFormError('Title is required');
    const interval = Math.max(1, parseInt(form.interval_n, 10) || 1);
    if (form.freq === 'weekly' && form.byweekday.length === 0) {
      return setFormError('Pick at least one weekday');
    }
    const bymonthday = Math.min(31, Math.max(1, parseInt(form.bymonthday, 10) || 1));

    setSaving(true);
    setFormError(null);
    const payload = {
      client_id: form.client_id,
      title: form.title.trim(),
      description: form.description.trim() || null,
      assigned_to: tab === 'operator' ? form.assigned_to : null,
      freq: form.freq,
      interval_n: interval,
      byweekday: form.freq === 'weekly' ? form.byweekday : null,
      bymonthday: form.freq === 'monthly' ? bymonthday : null,
      start_date: form.start_date || formatForDateInput(getCurrentDate()),
      end_date: form.end_date || null,
      auto_approve: tab === 'agentic' ? form.auto_approve : false,
    };
    const { error } = editingId
      ? await supabase.from('recurring_tasks').update(payload).eq('id', editingId)
      : await supabase.from('recurring_tasks').insert({ ...payload, kind: tab, active: true });
    setSaving(false);
    if (error) {
      console.error('Error saving recurring task:', error);
      setFormError('Failed to save. Check the console for details.');
      return;
    }
    closePanel();
    await fetchAll();
  };

  const handleToggleActive = async (t: RecurringTask, next: boolean) => {
    setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, active: next } : x)));
    const { error } = await supabase.from('recurring_tasks').update({ active: next }).eq('id', t.id);
    if (error) {
      console.error('Error toggling recurring task:', error);
      await fetchAll();
    }
  };

  const handleDelete = async (id: string) => {
    setConfirmDeleteId(null);
    const { error } = await supabase.from('recurring_tasks').delete().eq('id', id);
    if (error) console.error('Error deleting recurring task:', error);
    await fetchAll();
  };

  const handleGenerate = async () => {
    const days = Math.max(1, parseInt(genDays, 10) || 7);
    setGenerating(true);
    setGenResult(null);
    const { data, error } = await supabase.rpc('generate_recurring_tasks_next_days', { p_days: days });
    setGenerating(false);
    if (error) {
      console.error('Error generating recurring tasks:', error);
      setGenResult('Generation failed — check the console.');
      return;
    }
    setGenResult(`Created ${data ?? 0} task${data === 1 ? '' : 's'} through the next ${days} days.`);
    await fetchAll();
  };

  const handleGenerateAgenticToday = async () => {
    setGenerating(true);
    setGenResult(null);
    const { data, error } = await supabase.rpc('generate_recurring_agentic_tasks_today');
    setGenerating(false);
    if (error) {
      console.error('Error generating agentic tasks:', error);
      setGenResult('Generation failed — check the console.');
      return;
    }
    setGenResult(`Enqueued ${data ?? 0} spec job${data === 1 ? '' : 's'} for today.`);
    await fetchAll();
  };

  const visibleTemplates = useMemo(
    () => templates.filter((t) => t.kind === tab),
    [templates, tab]
  );

  const columns: TableColumn<RecurringTask>[] = useMemo(
    () => [
      {
        key: 'title',
        label: 'Task',
        render: (_, t) => (
          <div style={{ minWidth: 0 }}>
            <div style={{ color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.title}
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              {t.client?.name ?? '—'}
            </div>
          </div>
        ),
      },
      { key: 'cadence', label: 'Cadence', render: (_, t) => <span>{cadenceLabel(t)}</span> },
      tab === 'operator'
        ? {
            key: 'assignee',
            label: 'Assignee',
            render: (_, t) => <span>{users.find((u) => u.id === t.assigned_to)?.name ?? '—'}</span>,
          }
        : {
            key: 'mode',
            label: 'Mode',
            render: (_, t) =>
              t.auto_approve ? <Badge tone="up" dot>Auto-approve</Badge> : <Badge tone="neutral">Normal</Badge>,
          },
      {
        key: 'generated',
        label: 'Generated Through',
        render: (_, t) => (
          <span className="t-num">{formatDate(`${t.generated_through}T12:00:00`, 'MMM d')}</span>
        ),
      },
      {
        key: 'active',
        label: 'Active',
        render: (_, t) => (
          <Switch checked={t.active} onChange={(next) => handleToggleActive(t, next)} />
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (_, t) =>
          confirmDeleteId === t.id ? (
            <span style={{ display: 'inline-flex', gap: '6px' }}>
              <Button size="sm" variant="danger" onClick={() => handleDelete(t.id)}>
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteId(null)}>
                Keep
              </Button>
            </span>
          ) : (
            <KebabMenu
              actions={[
                { label: 'Edit', icon: faPen, onClick: () => setEditParam(t.id) },
                { label: 'Delete', icon: faTrash, danger: true, onClick: () => setConfirmDeleteId(t.id) },
              ]}
            />
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, confirmDeleteId, tab]
  );

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Recurring"
          count={visibleTemplates.filter((t) => t.active).length}
          actions={
            <Button size="sm" onClick={openCreate}>
              {tab === 'operator' ? 'New Recurring Task' : 'New Agentic Task'}
            </Button>
          }
        />

        {/* Sub menu */}
        <div style={{ marginBottom: 'var(--space-5)' }}>
          <Tabs
            tabs={TAB_DEFS.map((t) => t.label)}
            active={TAB_DEFS.find((t) => t.value === tab)?.label ?? 'Operator Tasks'}
            onChange={(label) => {
              const def = TAB_DEFS.find((t) => t.label === label);
              if (def) {
                setTab(def.value);
                setGenResult(null);
                setConfirmDeleteId(null);
              }
            }}
          />
        </div>

        {/* Generation controls */}
        {tab === 'operator' ? (
          <Card
            eyebrow="Generation"
            title="Build upcoming tasks"
            style={{ marginBottom: 'var(--space-5)' }}
          >
            <p style={{ margin: '0 0 12px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Every Friday morning the scheduler creates next week's tasks (Sunday through Saturday)
              from the active templates below. Nothing is generated further out, so client task lists
              stay short. Use this to build sooner or further ahead — dates already generated are
              never duplicated.
            </p>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
              <Input
                label="Days ahead"
                type="number"
                min={1}
                max={90}
                value={genDays}
                onChange={(e) => setGenDays(e.target.value)}
                style={{ width: '110px' }}
              />
              <Button variant="secondary" onClick={handleGenerate} disabled={generating}>
                {generating ? 'Generating…' : 'Generate now'}
              </Button>
              {genResult ? (
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', paddingBottom: '8px' }}>
                  {genResult}
                </span>
              ) : null}
            </div>
          </Card>
        ) : (
          <Card
            eyebrow="Generation"
            title="Nightly spec queue"
            style={{ marginBottom: 'var(--space-5)' }}
          >
            <p style={{ margin: '0 0 12px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Every evening at 6pm MST, anything below scheduled for that day is enqueued on the
              agents' spec queue (with a backing client task). Auto-approve templates skip spec
              review and go straight to the build queue when their spec is ready. Days already
              generated are never duplicated.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Button variant="secondary" onClick={handleGenerateAgenticToday} disabled={generating}>
                {generating ? 'Generating…' : "Run today's generation now"}
              </Button>
              {genResult ? (
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                  {genResult}
                </span>
              ) : null}
            </div>
          </Card>
        )}

        {/* Templates */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <div
              className="animate-spin w-7 h-7 border-2 rounded-full"
              style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
            />
          </div>
        ) : visibleTemplates.length === 0 ? (
          <Card>
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              {tab === 'operator'
                ? 'No recurring tasks yet. Create one and the Friday scheduler takes it from there.'
                : 'No agentic tasks yet. Create one and the 6pm scheduler queues it onto the spec queue.'}
            </div>
          </Card>
        ) : (
          <>
            <div className="hidden md:block">
              <Table columns={columns} rows={visibleTemplates} />
            </div>
            {/* Phones: one card per template instead of a 1,000px table whose toggle and menu sat
                off-screen */}
            <div
              className="md:hidden"
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-rest)',
              }}
            >
              {visibleTemplates.map((t, i) => (
                <div
                  key={t.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px 8px 12px 14px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border-hairline)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: 'var(--text-body)', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], overflowWrap: 'anywhere' }}>
                      {t.title}
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
                      {t.client?.name ?? '—'}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '4px 10px',
                        marginTop: '6px',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      <span>{cadenceLabel(t)}</span>
                      {tab === 'operator' ? (
                        <span>{users.find((u) => u.id === t.assigned_to)?.name ?? 'Unassigned'}</span>
                      ) : t.auto_approve ? (
                        <Badge tone="up" dot>Auto-approve</Badge>
                      ) : null}
                      <span className="t-num" style={{ color: 'var(--text-faint)' }}>
                        through {formatDate(`${t.generated_through}T12:00:00`, 'MMM d')}
                      </span>
                    </div>
                    {confirmDeleteId === t.id ? (
                      <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                        <Button size="sm" variant="danger" onClick={() => handleDelete(t.id)}>
                          Delete
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteId(null)}>
                          Keep
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    <Switch checked={t.active} onChange={(next) => handleToggleActive(t, next)} />
                    <KebabMenu
                      actions={[
                        { label: 'Edit', icon: faPen, onClick: () => setEditParam(t.id) },
                        { label: 'Delete', icon: faTrash, danger: true, onClick: () => setConfirmDeleteId(t.id) },
                      ]}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Create / edit */}
      <SidePanel
        open={panelOpen}
        onClose={closePanel}
        eyebrow={tab === 'operator' ? 'Recurring · Operator' : 'Recurring · Agentic'}
        title={
          editingId
            ? tab === 'operator' ? 'Edit Recurring Task' : 'Edit Agentic Task'
            : tab === 'operator' ? 'New Recurring Task' : 'New Agentic Task'
        }
        width={460}
        footer={
          <>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create'}
            </Button>
            <Button variant="ghost" onClick={closePanel} disabled={saving}>
              Cancel
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {formError ? (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                color: 'var(--danger-400)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {formError}
            </div>
          ) : null}

          <SearchableSelect
            label="Client"
            placeholder="Pick a client..."
            searchPlaceholder="Search clients..."
            options={clients.map((c) => ({ value: c.id, label: c.name }))}
            value={form.client_id}
            onChange={(v) => setForm((f) => ({ ...f, client_id: v }))}
          />

          <Input
            label="Title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Publish weekly blog post"
          />

          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Description</div>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="ck-textarea"
              placeholder={
                tab === 'operator'
                  ? 'Optional details copied onto every generated task…'
                  : 'Context for the agent — becomes the queue note on every generated spec job…'
              }
            />
          </div>

          {tab === 'operator' ? (
            <Dropdown
              label="Assign To"
              placeholder="Unassigned"
              options={[{ value: '', label: 'Unassigned' }, ...users.map((u) => ({ value: u.id, label: u.name }))]}
              value={form.assigned_to || ''}
              onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value || null }))}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '10px 12px',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>Auto Approve</div>
                <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
                  Skips spec review — jumps straight to the build queue when the spec is ready.
                </div>
              </div>
              <Switch
                checked={form.auto_approve}
                onChange={(next) => setForm((f) => ({ ...f, auto_approve: next }))}
              />
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <Dropdown
              label="Repeats"
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
              value={form.freq}
              onChange={(e) => setForm((f) => ({ ...f, freq: e.target.value as Freq }))}
            />
            <Input
              label={form.freq === 'daily' ? 'Every N days' : form.freq === 'weekly' ? 'Every N weeks' : 'Every N months'}
              type="number"
              min={1}
              max={12}
              value={form.interval_n}
              onChange={(e) => setForm((f) => ({ ...f, interval_n: e.target.value }))}
            />
          </div>

          {form.freq === 'weekly' ? (
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '7px' }}>On days</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {DAY_LABELS.map((label, d) => {
                  const on = form.byweekday.includes(d);
                  return (
                    <button
                      key={label}
                      type="button"
                      className="pressable"
                      onClick={() => toggleWeekday(d)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 'var(--radius-sm)',
                        border: `1px solid ${on ? 'var(--accent)' : 'var(--border-hairline)'}`,
                        background: on ? 'var(--accent-soft-hover)' : 'var(--surface-raised)',
                        color: on ? 'var(--accent-text)' : 'var(--text-muted)',
                        fontSize: 'var(--text-xs)',
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {form.freq === 'monthly' ? (
            <Input
              label="Day of month"
              type="number"
              min={1}
              max={31}
              value={form.bymonthday}
              onChange={(e) => setForm((f) => ({ ...f, bymonthday: e.target.value }))}
            />
          ) : null}

          {/* Stacked on phones: two native date inputs don't fit side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '10px' }}>
            <Input
              label="Starts"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
            />
            <Input
              label="Ends (optional)"
              type="date"
              value={form.end_date}
              onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
            />
          </div>

          {editingId ? (
            <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
              Changes apply to future generation only — tasks already created stay as they are.
            </p>
          ) : null}
        </div>
      </SidePanel>
    </div>
  );
}
